import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'peo_quote_draft';
const AUTO_SAVE_INTERVAL = 5000; // 5 seconds

// Use sessionStorage for sensitive draft data (cleared when tab closes)
// This is more secure than localStorage as it's not persisted across sessions
const storage = typeof window !== 'undefined' ? window.sessionStorage : null;

/**
 * Hook for managing quote draft auto-backup to localStorage
 * Provides:
 * - Auto-save to localStorage every 5 seconds when data changes
 * - Detection of unsaved work on mount
 * - Restore functionality
 * - Before-unload warning
 * - Unsaved changes indicator
 */
export function useQuoteDraftBackup({
  // Current form state
  clientName,
  currentProvider,
  preparedBy,
  quoteDate,
  lineItems,
  proposedRates,
  adminFeeType,
  adminFeeValue,
  proposedAdminFeeType,
  proposedAdminFeeValue,
  adpUnbundled,
  serviceFees,
  // Current saved quote ID (null if new/unsaved)
  currentQuoteId,
  // Callback to restore state
  onRestore,
}) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [savedDraft, setSavedDraft] = useState(null);
  const lastSavedRef = useRef(null);
  const isRestoringRef = useRef(false);

  // Create a snapshot of current form state
  const getCurrentSnapshot = useCallback(() => ({
    clientName,
    currentProvider,
    preparedBy,
    quoteDate,
    lineItems,
    proposedRates,
    adminFeeType,
    adminFeeValue,
    proposedAdminFeeType,
    proposedAdminFeeValue,
    adpUnbundled,
    serviceFees,
    currentQuoteId,
    timestamp: Date.now(),
  }), [
    clientName, currentProvider, preparedBy, quoteDate,
    lineItems, proposedRates, adminFeeType, adminFeeValue,
    proposedAdminFeeType, proposedAdminFeeValue, adpUnbundled,
    serviceFees, currentQuoteId
  ]);

  // Check if current state differs from last saved
  const checkForChanges = useCallback((snapshot) => {
    // If we're in the middle of restoring, don't mark as changed
    if (isRestoringRef.current) return false;
    
    // If no data entered yet, no unsaved changes
    if (!snapshot.clientName && !snapshot.lineItems?.length && !snapshot.adminFeeValue) {
      return false;
    }
    
    // Compare with last saved state
    if (!lastSavedRef.current) {
      // No saved state - if there's any meaningful data, it's unsaved
      return !!(snapshot.clientName || snapshot.lineItems?.length > 0);
    }
    
    // Deep comparison of relevant fields
    const saved = lastSavedRef.current;
    return (
      snapshot.clientName !== saved.clientName ||
      snapshot.currentProvider !== saved.currentProvider ||
      snapshot.preparedBy !== saved.preparedBy ||
      snapshot.quoteDate !== saved.quoteDate ||
      snapshot.adminFeeType !== saved.adminFeeType ||
      snapshot.adminFeeValue !== saved.adminFeeValue ||
      snapshot.proposedAdminFeeType !== saved.proposedAdminFeeType ||
      snapshot.proposedAdminFeeValue !== saved.proposedAdminFeeValue ||
      snapshot.adpUnbundled !== saved.adpUnbundled ||
      JSON.stringify(snapshot.lineItems) !== JSON.stringify(saved.lineItems) ||
      JSON.stringify(snapshot.proposedRates) !== JSON.stringify(saved.proposedRates) ||
      JSON.stringify(snapshot.serviceFees) !== JSON.stringify(saved.serviceFees)
    );
  }, []);

  // Save draft to sessionStorage
  const saveDraft = useCallback(() => {
    if (!storage) return;
    
    const snapshot = getCurrentSnapshot();
    
    // Only save if there's meaningful data
    if (snapshot.clientName || snapshot.lineItems?.length > 0 || snapshot.adminFeeValue) {
      try {
        storage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
      } catch (e) {
        console.warn('Failed to save draft to sessionStorage:', e);
      }
    }
    
    setHasUnsavedChanges(checkForChanges(snapshot));
  }, [getCurrentSnapshot, checkForChanges]);

  // Clear draft from sessionStorage
  const clearDraft = useCallback(() => {
    if (!storage) return;
    
    try {
      storage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to clear draft from sessionStorage:', e);
    }
    setHasUnsavedChanges(false);
  }, []);

  // Mark current state as "saved" (called after successful API save)
  const markAsSaved = useCallback(() => {
    lastSavedRef.current = getCurrentSnapshot();
    setHasUnsavedChanges(false);
    clearDraft();
  }, [getCurrentSnapshot, clearDraft]);

  // Restore draft from localStorage
  const restoreDraft = useCallback(() => {
    if (savedDraft && onRestore) {
      isRestoringRef.current = true;
      onRestore(savedDraft);
      setShowRestorePrompt(false);
      setSavedDraft(null);
      clearDraft();
      // Reset flag after a short delay to allow state to settle
      setTimeout(() => {
        isRestoringRef.current = false;
      }, 500);
    }
  }, [savedDraft, onRestore, clearDraft]);

  // Dismiss restore prompt without restoring
  const dismissRestorePrompt = useCallback(() => {
    setShowRestorePrompt(false);
    setSavedDraft(null);
    clearDraft();
  }, [clearDraft]);

  // Check for saved draft on mount
  useEffect(() => {
    if (!storage) return;
    
    try {
      const stored = storage.getItem(STORAGE_KEY);
      if (stored) {
        const draft = JSON.parse(stored);
        // Only show restore prompt if draft has meaningful data
        // and is less than 24 hours old
        const isRecent = Date.now() - draft.timestamp < 24 * 60 * 60 * 1000;
        const hasMeaningfulData = draft.clientName || draft.lineItems?.length > 0;
        
        if (isRecent && hasMeaningfulData) {
          setSavedDraft(draft);
          setShowRestorePrompt(true);
        } else {
          // Draft is too old or empty, clear it
          clearDraft();
        }
      }
    } catch (e) {
      console.warn('Failed to load draft from sessionStorage:', e);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save to localStorage periodically
  useEffect(() => {
    const interval = setInterval(() => {
      saveDraft();
    }, AUTO_SAVE_INTERVAL);

    return () => clearInterval(interval);
  }, [saveDraft]);

  // Update unsaved changes indicator when state changes
  useEffect(() => {
    const snapshot = getCurrentSnapshot();
    setHasUnsavedChanges(checkForChanges(snapshot));
  }, [getCurrentSnapshot, checkForChanges]);

  // Before-unload warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  return {
    hasUnsavedChanges,
    showRestorePrompt,
    savedDraft,
    restoreDraft,
    dismissRestorePrompt,
    markAsSaved,
    clearDraft,
  };
}
